import type {DescriptionLang} from './description';
import {z} from 'zod';
import {unitsToQuantity} from './warehouseReceipts';
import {vnMonth} from './vnTime';

export const KPI_IDS=[
 'delivery_on_time','delivery_overdue','delivery_lead_time',
 'supplier_on_time','purchase_overdue',
 'qc_iqc_acceptance','qc_iqc_pending','qc_ncr_open',
 'warehouse_available','warehouse_held','inventory_low_stock',
 'production_open','production_wip',
 'forecast_demand',
 'portal_open_po','portal_iqc_pending','portal_ncr_open','portal_on_time','portal_iqc_acceptance',
] as const;
export type KpiId=(typeof KPI_IDS)[number];

/**
 * Bộ chỉ số chốt 16/09/2026 theo chuẩn vận hành (SCOR / APICS, bộ KPI mặc định
 * của Odoo/NetSuite) cho công ty thương mại kiêm sản xuất nhẹ, KHÔNG có tiền:
 *   giao hàng (OTD, quá hạn, lead time) · mua hàng (NCC đúng hạn, PO quá hạn) ·
 *   chất lượng (IQC đạt, chờ IQC, NCR mở) · tồn kho (khả dụng, giữ, dưới ngưỡng) ·
 *   sản xuất (MO mở, WIP) · dự báo (SMA-3 kèm MAPE).
 * Tỷ lệ tính trên cửa sổ trượt KPI_WINDOW_DAYS; thiếu mẫu thì "không có số",
 * không phải 0% hay 100%. Mỗi ô công bố công thức + nguồn (KPI-R01).
 */
export const KPI_WINDOW_DAYS=90;
/** Ngưỡng cảnh báo (%, cảnh báo khi THẤP hơn). Mặc định theo thông lệ: OTD 95, NCC 90, IQC 98. */
export const KPI_THRESHOLDS={delivery_on_time:95,supplier_on_time:90,qc_iqc_acceptance:98} as const;
export type KpiGroup='delivery'|'purchasing'|'quality'|'inventory'|'production'|'forecast'|'portal';
export const KPI_GROUP_ORDER:readonly KpiGroup[]=['delivery','purchasing','quality','inventory','production','forecast'];
export const KPI_GROUP:Record<KpiId,KpiGroup>={
 delivery_on_time:'delivery',delivery_overdue:'delivery',delivery_lead_time:'delivery',
 supplier_on_time:'purchasing',purchase_overdue:'purchasing',
 qc_iqc_acceptance:'quality',qc_iqc_pending:'quality',qc_ncr_open:'quality',
 warehouse_available:'inventory',warehouse_held:'inventory',inventory_low_stock:'inventory',
 production_open:'production',production_wip:'production',
 forecast_demand:'forecast',
 portal_open_po:'portal',portal_iqc_pending:'portal',portal_ncr_open:'portal',portal_on_time:'portal',portal_iqc_acceptance:'portal',
};
export const KPI_CATALOG:Record<KpiId,{formula:string;source:string}>={
 delivery_on_time:{formula:'Proforma invoices with a Delivered delivery note in the last 90 days: % whose first delivered date <= the PI delivery date. PIs without a delivery date are gaps, not late.',source:'proforma_invoices.delivery_date, delivery_notes(delivery_status=Delivered)'},
 delivery_overdue:{formula:'COUNT proforma invoices whose delivery date passed within the last 90 days, with no Delivered delivery note and not paid in full.',source:'proforma_invoices, delivery_notes, pi_payments'},
 delivery_lead_time:{formula:'MEDIAN days from PI date to first Delivered delivery note, same 90-day sample as on-time delivery.',source:'proforma_invoices.date, delivery_notes'},
 supplier_on_time:{formula:'Confirmed goods receipts in the last 90 days: % received on or before the PO expected delivery date. Receipts whose PO has no expected date are gaps.',source:'warehouse_receipts.received_date, purchase_orders.expected_delivery_date'},
 purchase_overdue:{formula:'COUNT purchase orders Sent/Confirmed/Partially Delivered whose expected delivery date passed within the last 90 days.',source:'purchase_orders'},
 qc_iqc_acceptance:{formula:'Approved, non-voided IQC reports in the last 90 days: accepted units / (accepted + rejected units) × 100.',source:'iqc_reports, iqc_dispositions'},
 warehouse_available:{formula:'SUM(lot_positions.available_units) grouped by product_id+unit from lot_entry_sources. Mixed units are not added. Positions without identity are gaps, not zero.',source:'lot_positions JOIN lot_entry_sources'},
 warehouse_held:{formula:'SUM(lot_positions.rejected_units)+SUM(lot_reserved_positions.reserved_units) as separate drill-down rows, still per product+unit.',source:'lot_positions, lot_reserved_positions'},
 production_open:{formula:'COUNT manufacturing_orders status=Released, not archived, without manufacturing_closures.',source:'manufacturing_orders LEFT manufacturing_closures'},
 production_wip:{formula:'SUM(production_material_custody.custody_units) per product+unit. Mixed units are not a single number.',source:'production_material_custody'},
 qc_iqc_pending:{formula:'COUNT confirmed warehouse_receipts with no non-voided Approved IQC. Zero means every confirmed receipt has an approved report, not a missing ledger.',source:'warehouse_receipts, iqc_reports, iqc_voids'},
 qc_ncr_open:{formula:'COUNT ncr_documents status=Open.',source:'ncr_documents'},
 portal_open_po:{formula:'COUNT purchase_orders for the bound supplier with status=Confirmed. No other supplier is included. Amounts and unit prices are omitted.',source:'purchase_orders.supplier_id'},
 portal_iqc_pending:{formula:'COUNT confirmed warehouse_receipts of the bound supplier with no non-voided Approved IQC.',source:'warehouse_receipts.supplier_id, iqc_reports'},
 portal_ncr_open:{formula:'COUNT ncr_documents status=Open whose source snapshot supplierId equals the bound supplier.',source:'ncr_documents.source_snapshot.supplierId'},
 inventory_low_stock:{formula:'COUNT active products with reorder_point > 0 and LOT available stock (same unit, active warehouses) < reorder_point.',source:'product_lot_stock (Kho & LOT), products.reorder_point'},
 forecast_demand:{formula:'Per product+unit: monthly PI quantity over the last 12 months; forecast for the current month = simple moving average of the last 3 complete months (SMA-3). Shown only with >= 4 months of history and a MAPE from a rolling backtest of >= 3 months. Tile = number of products with a forecast; drill-down lists forecast and MAPE.',source:'pi_items.quantity grouped by month of proforma_invoices.date'},
 portal_on_time:{formula:'Confirmed receipts of the bound supplier in the last 90 days: % received on or before the PO expected delivery date.',source:'warehouse_receipts.supplier_id, purchase_orders.expected_delivery_date'},
 portal_iqc_acceptance:{formula:'Approved IQC reports on the bound supplier\'s receipts in the last 90 days: accepted units / (accepted + rejected) × 100.',source:'iqc_reports, iqc_dispositions, warehouse_receipts.supplier_id'},
};


export interface KpiGap {reason:string;detail:string;}
export interface KpiDrillDown {id:string;label:string;href:string;quantity:string;unit:string;}
export interface KpiMetric {
 id:KpiId;formula:string;source:string;asOf:number;status:'present'|'missing';
 value:string|null;unit:string|null;missingReason:string|null;gaps:KpiGap[];drillDown:KpiDrillDown[];
}
export interface KpiAlert {id:string;metricId:KpiId;severity:'info'|'warn';status:'present'|'missing';message:string;}
export interface KpiReport {asOf:number;metrics:KpiMetric[];alerts:KpiAlert[];}

export interface WarehousePositionRow {
 entryId:string;productId:string|null;productCode:string|null;unit:string|null;
 availableUnits:number;rejectedUnits:number;reservedUnits:number;warehouseId:string;location:string;lotNumber:string|null;
}
export interface WipRow {productId:string;productCode:string;unit:string|null;custodyUnits:number;}

export function presentMetric(id:KpiId,asOf:number,value:string,unit:string,drillDown:KpiDrillDown[],gaps:KpiGap[]=[]):KpiMetric{
 const cat=KPI_CATALOG[id];
 return {id,formula:cat.formula,source:cat.source,asOf,status:'present',value,unit,missingReason:null,gaps,drillDown};
}
export function missingMetric(id:KpiId,asOf:number,reason:string,gaps:KpiGap[]=[],drillDown:KpiDrillDown[]=[]):KpiMetric{
 const cat=KPI_CATALOG[id];
 return {id,formula:cat.formula,source:cat.source,asOf,status:'missing',value:null,unit:null,missingReason:reason,gaps,drillDown};
}

function unitKey(row:{productId:string|null;unit:string|null}):string|null{
 if(!row.productId||!row.unit)return null;
 return `${row.productId}\u0000${row.unit}`;
}

export function aggregateWarehouse(id:Extract<KpiId,'warehouse_available'|'warehouse_held'>,asOf:number,rows:WarehousePositionRow[],pick:(row:WarehousePositionRow)=>number):KpiMetric{
 const gaps:KpiGap[]=[];const buckets=new Map<string,{productId:string;productCode:string;unit:string;units:number;lines:KpiDrillDown[]}>();
 for(const row of rows){
  const key=unitKey(row);
  if(!key){if(pick(row)!==0)gaps.push({reason:'unidentified_source',detail:row.entryId});continue;}
  const n=pick(row);const current=buckets.get(key)??{productId:row.productId!,productCode:row.productCode??row.productId!,unit:row.unit!,units:0,lines:[]};
  current.units+=n;
  current.lines.push({id:`${row.entryId}:${row.warehouseId}:${row.location}`,label:`${current.productCode} ${row.lotNumber??row.entryId} @ ${row.location}`,href:`/stock-moves`,quantity:unitsToQuantity(n),unit:row.unit!});
  buckets.set(key,current);
 }
 const groups=[...buckets.values()];
 const drillDown=groups.flatMap(g=>g.lines);
 if(gaps.length)return missingMetric(id,asOf,'unidentified_source',gaps,drillDown);
 if(groups.length===0)return presentMetric(id,asOf,'0','',[]);
 const units=new Set(groups.map(g=>g.unit));
 if(units.size>1)return missingMetric(id,asOf,'mixed_unit',[{reason:'mixed_unit',detail:[...units].join(',')}],drillDown);
 const total=groups.reduce((n,g)=>n+g.units,0);
 return presentMetric(id,asOf,unitsToQuantity(total),[...units][0]!,drillDown);
}

export function aggregateWip(asOf:number,rows:WipRow[]):KpiMetric{
 if(rows.length===0)return presentMetric('production_wip',asOf,'0','',[]);
 if(rows.some(r=>!r.unit))return missingMetric('production_wip',asOf,'unidentified_source',rows.filter(r=>!r.unit).map(r=>({reason:'unidentified_source',detail:r.productId})));
 const units=new Set(rows.map(r=>r.unit!));
 const drillDown=rows.map(r=>({id:r.productId,label:r.productCode,href:'/production/materials',quantity:unitsToQuantity(r.custodyUnits),unit:r.unit!}));
 if(units.size>1)return missingMetric('production_wip',asOf,'mixed_unit',[{reason:'mixed_unit',detail:[...units].join(',')}],drillDown);
 return presentMetric('production_wip',asOf,unitsToQuantity(rows.reduce((n,r)=>n+r.custodyUnits,0)),[...units][0]!,drillDown);
}

const DAY_MS=86400000;
const dayOf=(ms:number)=>Math.floor(ms/DAY_MS);
/** 'YYYY-MM-DD' → ms đầu ngày UTC; chuỗi hỏng → null. */
export function dayToMs(day:string|null|undefined):number|null{
 if(!day)return null;const ms=Date.parse(`${day}T00:00:00Z`);return Number.isFinite(ms)?ms:null;
}

export interface OnTimeRow{id:string;label:string;href:string;promisedAt:number|null;actualAt:number|null;}
/**
 * Tỷ lệ đúng hạn (%): so sánh theo NGÀY, không theo giờ. Dòng thiếu ngày hẹn là
 * khoảng trống (gap) chứ không phải trễ; không có mẫu nào thì "không có số".
 * Đối soát = những dòng trễ, kèm số ngày trễ.
 */
export function onTimeMetric(id:Extract<KpiId,'delivery_on_time'|'supplier_on_time'|'portal_on_time'>,asOf:number,rows:OnTimeRow[]):KpiMetric{
 const gaps:KpiGap[]=rows.filter(r=>r.promisedAt==null).map(r=>({reason:'no_promise',detail:r.label}));
 const sample=rows.filter(r=>r.promisedAt!=null&&r.actualAt!=null);
 if(sample.length===0)return missingMetric(id,asOf,'no_sample',gaps);
 const late=sample.filter(r=>dayOf(r.actualAt!)>dayOf(r.promisedAt!));
 const rate=(sample.length-late.length)/sample.length*100;
 const drillDown=late.map(r=>({id:r.id,label:r.label,href:r.href,quantity:String(dayOf(r.actualAt!)-dayOf(r.promisedAt!)),unit:'day'}));
 return presentMetric(id,asOf,rate.toFixed(1),'%',drillDown,gaps);
}

export interface LeadTimeRow{id:string;label:string;href:string;startAt:number|null;endAt:number|null;}
/** Lead time = TRUNG VỊ số ngày (trung bình bị một đơn chậm bất thường kéo lệch). */
export function leadTimeMetric(asOf:number,rows:LeadTimeRow[]):KpiMetric{
 const sample=rows.filter(r=>r.startAt!=null&&r.endAt!=null&&r.endAt>=r.startAt).map(r=>({...r,days:dayOf(r.endAt!)-dayOf(r.startAt!)}));
 if(sample.length===0)return missingMetric('delivery_lead_time',asOf,'no_sample');
 const sorted=[...sample].sort((a,b)=>a.days-b.days);const mid=Math.floor(sorted.length/2);
 const median=sorted.length%2?sorted[mid]!.days:Math.round((sorted[mid-1]!.days+sorted[mid]!.days)/2);
 const drillDown=[...sorted].reverse().slice(0,12).map(r=>({id:r.id,label:r.label,href:r.href,quantity:String(r.days),unit:'day'}));
 return presentMetric('delivery_lead_time',asOf,String(median),'day',drillDown);
}

/** Đếm chứng từ, kèm chính các chứng từ đó để đối soát. Rỗng là 0 THẬT (đã đọc sổ). */
export function countMetric(id:KpiId,asOf:number,rows:KpiDrillDown[],unit:string):KpiMetric{
 return presentMetric(id,asOf,String(rows.length),unit,rows.slice(0,50));
}

export interface AcceptanceRow{id:string;label:string;href:string;accepted:number;rejected:number;unit:string|null;}
/** Tỷ lệ IQC đạt (%) theo SỐ LƯỢNG đã kiểm; không có phiếu nào thì "không có số". */
export function acceptanceMetric(id:Extract<KpiId,'qc_iqc_acceptance'|'portal_iqc_acceptance'>,asOf:number,rows:AcceptanceRow[]):KpiMetric{
 const total=rows.reduce((n,r)=>n+r.accepted+r.rejected,0);
 if(total<=0)return missingMetric(id,asOf,'no_sample');
 const accepted=rows.reduce((n,r)=>n+r.accepted,0);
 const drillDown=rows.filter(r=>r.rejected>0).map(r=>({id:r.id,label:r.label,href:r.href,quantity:unitsToQuantity(r.rejected),unit:r.unit??''}));
 return presentMetric(id,asOf,(accepted/total*100).toFixed(1),'%',drillDown);
}

export interface DemandRow{productId:string;productCode:string;unit:string|null;month:string;quantity:number;}
export const FORECAST={historyMonths:12,minHistory:4,window:3,minBacktest:3,backtestMonths:6} as const;
const monthIndex=(ym:string)=>{const [y,m]=ym.split('-').map(Number);return y!*12+(m!-1);};
const ymOf=(idx:number)=>`${Math.floor(idx/12)}-${String(idx%12+1).padStart(2,'0')}`;
export interface ProductForecast{productId:string;productCode:string;unit:string;targetMonth:string;forecast:number;mape:number;}
/**
 * Dự báo nhu cầu theo chuẩn tối thiểu của APICS cho doanh nghiệp nhỏ: SMA-3 trên
 * số lượng đặt (PI) từng tháng, cho THÁNG HIỆN TẠI (tháng ngay sau tháng trọn
 * gần nhất), kèm MAPE từ backtest trượt — tức là chỉ hiện số khi đo được nó
 * sai bao nhiêu trong quá khứ. Chưa đủ lịch sử thì không có dự báo cho sản
 * phẩm đó; không sản phẩm nào đủ thì ô "không có số" (KPI-R02: không bịa số).
 */
export function forecastProducts(asOf:number,rows:DemandRow[]):ProductForecast[]{
 const cur=monthIndex(vnMonth(asOf));const lastFull=cur-1;
 const series=new Map<string,{productId:string;productCode:string;unit:string;byMonth:Map<number,number>}>();
 for(const r of rows){
  if(!r.unit||!(r.quantity>0))continue;
  const key=`${r.productId}\u0000${r.unit}`;const cell=series.get(key)??{productId:r.productId,productCode:r.productCode,unit:r.unit,byMonth:new Map()};
  const m=monthIndex(r.month);if(m>lastFull)continue;
  cell.byMonth.set(m,(cell.byMonth.get(m)??0)+r.quantity);series.set(key,cell);
 }
 const out:ProductForecast[]=[];
 for(const cell of series.values()){
  const first=Math.min(...cell.byMonth.keys());
  const hist:number[]=[];for(let m=first;m<=lastFull;m++)hist.push(cell.byMonth.get(m)??0);
  if(hist.length<FORECAST.minHistory)continue;
  const mean=(a:number[])=>a.reduce((n,x)=>n+x,0)/a.length;
  const errors:number[]=[];
  for(let k=Math.max(FORECAST.window,hist.length-FORECAST.backtestMonths);k<hist.length;k++){
   const actual=hist[k]!;if(actual<=0)continue;
   const pred=mean(hist.slice(k-FORECAST.window,k));errors.push(Math.abs(actual-pred)/actual);
  }
  if(errors.length<FORECAST.minBacktest)continue;
  out.push({productId:cell.productId,productCode:cell.productCode,unit:cell.unit,targetMonth:ymOf(cur),forecast:Math.round(mean(hist.slice(-FORECAST.window))),mape:mean(errors)*100});
 }
 return out.sort((a,b)=>b.forecast-a.forecast);
}
export function forecastMetric(asOf:number,rows:DemandRow[]=[]):KpiMetric{
 const list=forecastProducts(asOf,rows);
 if(list.length===0)return missingMetric('forecast_demand',asOf,'forecast_history',[{reason:'forecast_history',detail:`${rows.length} demand rows, no product with >= ${FORECAST.minHistory} months of history and ${FORECAST.minBacktest} backtest months`}]);
 const drillDown=list.slice(0,20).map(f=>({id:`${f.productId}:${f.unit}`,label:`${f.productCode} · ${f.targetMonth} · MAPE ${f.mape.toFixed(0)}%`,href:`/products/${f.productId}`,quantity:String(f.forecast),unit:f.unit}));
 return presentMetric('forecast_demand',asOf,String(list.length),'product',drillDown);
}

export function buildAlerts(metrics:KpiMetric[]):KpiAlert[]{
 const byId=new Map(metrics.map(m=>[m.id,m]));
 const alerts:KpiAlert[]=[];
 const below=(id:keyof typeof KPI_THRESHOLDS,message:string)=>{
  const m=byId.get(id);
  if(m?.status==='present'&&m.value!=null&&Number(m.value)<KPI_THRESHOLDS[id])alerts.push({id:`${id}-below`,metricId:id,severity:'warn',status:'present',message});
 };
 const nonZero=(id:KpiId,severity:'info'|'warn',message:string)=>{
  const m=byId.get(id);
  if(m?.status==='present'&&m.value&&m.value!=='0')alerts.push({id:`${id}-nonzero`,metricId:id,severity,status:'present',message});
 };
 below('delivery_on_time','late_delivery');nonZero('delivery_overdue','warn','overdue_pi');
 below('supplier_on_time','late_supplier');nonZero('purchase_overdue','warn','overdue_po');
 below('qc_iqc_acceptance','iqc_below');nonZero('qc_ncr_open','warn','open_ncr');nonZero('qc_iqc_pending','info','iqc_pending');
 const held=byId.get('warehouse_held');
 if(held?.status==='missing')alerts.push({id:'held-unknown',metricId:'warehouse_held',severity:'warn',status:'missing',message:'held_unknown'});
 else nonZero('warehouse_held','warn','held_stock');
 nonZero('inventory_low_stock','warn','low_stock');
 const forecast=byId.get('forecast_demand');
 if(forecast?.status==='missing')alerts.push({id:'forecast-history',metricId:'forecast_demand',severity:'info',status:'missing',message:forecast.missingReason??'forecast_history'});
 return alerts;
}

export const portalBindSchema=z.object({requestId:z.string().uuid(),userId:z.string().min(1).max(100),supplierId:z.string().min(1).max(100)});
export type PortalBindInput=z.infer<typeof portalBindSchema>;
export interface PortalBindingDto {id:string;userId:string;supplierId:string;supplierName:string;}
export interface PortalOrderLine {id:string;productCode:string;unit:string;orderedQuantity:string;receivedQuantity:string;}
export interface PortalOrderDto {id:string;poNumber:string;status:string;lines:PortalOrderLine[];}
/**
 * Đơn mua hàng in cho NHÀ CUNG CẤP — danh sách TRẮNG, không phải bản đầy đủ.
 *
 * Là đúng những gì Hisense vẫn gửi họ qua email: giá mua, số lượng, ngày giao,
 * điều khoản. KHÔNG có PI/khách hàng đứng sau đơn, yêu cầu mua nội bộ, ảnh chụp
 * giá, tên nhân viên lập. Máy chủ chỉ điền các trường ở đây, nên một trường nội
 * bộ thêm vào `PurchaseOrderDetailDto` sau này không tự lọt ra cổng.
 */
export interface PortalOrderPrintLine {
 lineNo:number;productCode:string|null;subRef:string|null;description:string|null;descriptionZh?:string|null;
 color:string|null;size:string|null;moq:number|null;quantity:number;unitPrice:number;totalPrice:number;remarks:string|null;lotNumber:string|null;
}
export interface PortalOrderPrintDto {
 id:string;poNumber:string;printLanguage:DescriptionLang;status:string;priority:string|null;
 orderDate:number|null;expectedDeliveryDate:number|null;currency:string;
 paymentTerms:string|null;shippingMethod:string|null;remarks:string|null;sourcePProcess?:boolean;
 supplierName:string|null;supplierAddress:string|null;supplierPhone:string|null;supplierEmail:string|null;supplierContact:string|null;
 subtotal:number;shipping:number;taxPercentage:number;tax:number;total:number;
 items:PortalOrderPrintLine[];
}
export interface PortalReceiptDto {id:string;receiptNo:string;poNumber:string;status:string;receivedDate:string;lotNumbers:string[];}
export interface PortalNcrDto {id:string;documentNo:string;status:string;title:string;lotNumber:string;productCode:string;}
export interface PortalDashboard {
 supplierId:string;supplierName:string;asOf:number;
 kpis:KpiMetric[];orders:PortalOrderDto[];receipts:PortalReceiptDto[];ncr:PortalNcrDto[];
}
/**
 * Một dòng trên bảng kê công nợ nhà cung cấp tự xem.
 *
 * Hai loại dòng (17/09/2026): HOÁ ĐƠN nhà cung cấp đã ghi sổ, và PHIẾU GHI NỢ đã ghi sổ (khoản phạt /
 * bồi thường từ khiếu nại nhà cung cấp, trừ vào tiền Hisense phải trả).
 *  - Hoá đơn: `amountMinor` tổng hoá đơn; `paidMinor` đã trả bằng phiếu chi đã ghi sổ; `deductedMinor`
 *    phần phiếu ghi nợ đã trừ vào; `balanceMinor` còn phải trả (≥ 0).
 *  - Phiếu ghi nợ: `amountMinor` số tiền phiếu (dương); `paidMinor` 0; `deductedMinor` phần của phiếu
 *    đã trừ vào hoá đơn; `balanceMinor` phần CHƯA trừ, mang dấu ÂM (≤ 0) — nhà cung cấp còn bị trừ tiếp.
 * Cộng `balanceMinor` của mọi dòng cùng loại tiền = số Hisense thực còn phải trả.
 *
 * `carriedOver` = dòng nằm NGOÀI cửa sổ 3 tháng nhưng vẫn hiện vì còn số dư. Giao diện phải đánh dấu rõ,
 * nếu không nhà cung cấp sẽ tưởng cửa sổ bị tính sai. Tiền là minor units (4dp) như mọi chỗ khác trong hệ.
 */
export interface SupplierPayableLine {
 kind:'Invoice'|'DebitNote';
 documentId:string;documentNo:string;currency:string;
 /** `YYYY-MM-DD`: ngày hoá đơn, hoặc ngày ghi sổ phiếu ghi nợ. */
 documentDate:string;
 amountMinor:number;paidMinor:number;deductedMinor:number;balanceMinor:number;
 /** Phiếu ghi nợ: số khiếu nại nhà cung cấp làm căn cứ. Hoá đơn: null. */
 reference:string|null;
 carriedOver:boolean;
}
export interface SupplierPayables {
 supplierId:string;
 /** Tên NCC in trên bảng kê — cổng không gọi được danh bạ, nên máy chủ gửi kèm. */
 supplierName:string;
 /** `YYYY-MM-01` — mốc đầu cửa sổ 3 tháng dương lịch. */
 windowStart:string;
 lines:SupplierPayableLine[];
 /** Tổng số dư theo loại tiền; ÂM nghĩa là phiếu ghi nợ chưa trừ hết lớn hơn số còn phải trả. */
 totals:{currency:string;balanceMinor:number}[];
}
